/**
 * Personas: who the clinic is writing for.
 *
 * A list of people, presented as people. Every other fact screen is a table
 * because a price or a qualification is a value; a persona is a person, and the
 * question this screen answers - "which of these am I writing for" - is answered
 * faster by recognising a face and a one-line description than by reading four
 * columns.
 *
 * A persona is also the only fact here that is *invented* rather than recorded.
 * Everything else states something true about the clinic and has to be typed by
 * somebody who knows it; a persona is a target reader the clinic makes up, so a
 * model may propose one. What the model returns is a suggestion in a form - the
 * writer edits it and saves, and it lands as DRAFT like every other fact. The
 * approval gate that decides what an article may say is untouched.
 */
import { confirmDialog } from '../components/AppDialog.tsx';
import { useId, useState } from 'react';
import { useLoaderData, useOutletContext, useRevalidator, useSearchParams } from 'react-router';
import {
  ApiError,
  del,
  imageUrl,
  patch,
  post,
  type CatalogItem,
  type ListResponse,
  type PlatformDefaultItem,
  type Session,
} from '../api.ts';
import { inScope, sessionScope, type FactPanelProps } from '../fact-panel.ts';
import { Card, Pill, StatusPill } from '../components/ui.tsx';
import { Modal, ModalForm } from '../components/Modal.tsx';
import { Field, FieldGroup } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';
import { ImageUpload } from '../components/ImageUpload.tsx';
import { Textarea } from '../components/Textarea.tsx';
import { AnimatePresence, listItem, m } from '../components/motion.tsx';
import { SkeletonPeople, usePendingScreen } from '../components/Skeleton.tsx';

interface Persona extends CatalogItem {
  storeName?: string | null;
  /** Set when this row was copied from one of JMC's suggestions. */
  originDefaultId?: string | null;
  label?: string;
  ageRange?: string;
  gender?: string;
  concerns?: string;
  tone?: string;
  notes?: string;
  aiBrief?: string;
  isDefault?: boolean;
  portraitSource?: 'generated' | 'uploaded' | null;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The blank form, so "new" and "reset" are the same thing. */
const EMPTY = {
  label: '',
  ageRange: '',
  gender: '',
  concerns: '',
  tone: '',
  notes: '',
  aiBrief: '',
};

type Form = typeof EMPTY;

function formFor(persona: Persona): Form {
  return {
    label: text(persona.label),
    ageRange: text(persona.ageRange),
    gender: text(persona.gender),
    concerns: text(persona.concerns),
    tone: text(persona.tone),
    notes: text(persona.notes),
    aiBrief: text(persona.aiBrief),
  };
}

/**
 * One persona as a tile.
 *
 * A GRID OF PEOPLE, not a list of rows. Every other fact screen is a table
 * because a price or a qualification is a value; a persona is a person, and the
 * question this screen answers - "which of these am I writing for" - is answered
 * by recognising a face and a sentence, which is what a tile shows and a
 * full-width row wastes six hundred pixels not showing.
 *
 * The portrait leads because it is the recognisable part. `concerns` is clamped
 * in CSS rather than cut at a character count: the stylesheet knows the rendered
 * width and a length check does not, and Japanese and Latin text differ by a
 * factor of three at the same character count.
 */
function PersonaTile({
  persona,
  /** Its place in the grid, which is how long it waits before arriving. */
  index,
  onOpen,
}: {
  persona: Persona;
  index: number;
  onOpen: () => void;
}) {
  const label = text(persona.label) || '（名称なし）';
  const facts = [text(persona.ageRange), text(persona.gender)].filter((part) => part !== '');

  return (
    <m.button
      className="ptile"
      type="button"
      // Opts out of the stylesheet's own entrance: see styles.css.
      data-motion
      layout
      {...listItem(index)}
      onClick={onOpen}
    >
      <span className="ptile__face">
        {typeof persona.photoId === 'string' ? (
          <img src={imageUrl(persona.photoId)} alt="" width={72} height={72} />
        ) : (
          <span className="ptile__blank" aria-hidden>
            {label.charAt(0)}
          </span>
        )}
      </span>

      <span className="ptile__label">{label}</span>

      {/* The two attributes that change how an article is worded, as chips: a
          40代 reader and a 20代 one are told the same thing differently. */}
      {facts.length > 0 && (
        <span className="ptile__facts">
          {facts.map((fact) => (
            <span key={fact} className="tag">
              {fact}
            </span>
          ))}
        </span>
      )}

      <span className="ptile__desc">
        {text(persona.concerns) || 'まだ内容がありません。開いて追加してください。'}
      </span>

      <span className="ptile__meta">
        {/*
          Which stores can use this one. First in the row because it decides
          whether editing it affects other stores' articles, which is the most
          consequential thing about any of these.
        */}
        {persona.storeId === null ? (
          <Pill>クリニック共通</Pill>
        ) : (
          <Pill tone="muted">{persona.storeName ?? '店舗'}</Pill>
        )}
        <StatusPill status={persona.status} />
        {persona.isDefault && <Pill tone="ok">既定</Pill>}
      </span>
    </m.button>
  );
}

/**
 * The persona as a sheet, the way this document is drawn everywhere else.
 *
 * A UX persona is a recognised format - a face, who they are, what they want,
 * what stops them, and a line in their own voice - and reading one is a
 * different act from filling one in. Opening a tile used to drop straight into
 * the form: eight inputs, every field equally loud, and no way to simply LOOK at
 * the reader you are about to write for. Now the sheet is what opens and 編集
 * is a button on it.
 *
 * The quote is `concerns` in the persona's own voice. It is the same text the
 * model receives; showing it as a quotation is what makes a page of fields read
 * as a person, and it is the one element of the standard format this data
 * already had and never displayed.
 */
function PersonaSheet({ persona }: { persona: Persona }) {
  const label = text(persona.label) || '（名称なし）';
  const facts = [text(persona.ageRange), text(persona.gender)].filter((part) => part !== '');
  /*
   * 悩み・関心 is the quotation above, so it is not repeated here.
   *
   * It is the same field: showing it as their voice AND again under a heading
   * put the longest text on the sheet twice, which reads as a rendering fault.
   * When there is no quote to show - a persona whose concerns are blank - the
   * heading comes back, so the gap is still visible as a gap.
   */
  const quote = text(persona.concerns);
  const sections: Array<[string, string, string]> = [
    ...(quote === ''
      ? ([['悩み・関心', '', '記事の切り口と、どの不安に先に答えるかを決めます']] as Array<
          [string, string, string]
        >)
      : []),
    ['希望トーン', text(persona.tone), '店舗の既定より優先して使われます'],
    ['備考', text(persona.notes), '触れてほしくない話題や、前提にしてよい知識'],
    ['AIへの指示', text(persona.aiBrief), '下書きを作るときの手がかり'],
  ];

  return (
    <div className="psheet">
      <div className="psheet__head">
        <span className="psheet__face">
          {typeof persona.photoId === 'string' ? (
            <img src={imageUrl(persona.photoId)} alt="" width={96} height={96} />
          ) : (
            <span className="ptile__blank" aria-hidden>
              {label.charAt(0)}
            </span>
          )}
        </span>
        <div>
          <p className="psheet__label">{label}</p>
          <div className="chips" style={{ marginBottom: 6 }}>
            {facts.map((fact) => (
              <span key={fact} className="tag">
                {fact}
              </span>
            ))}
          </div>
          <div className="chips">
            {persona.storeId === null ? (
              <Pill>クリニック共通</Pill>
            ) : (
              <Pill tone="muted">{persona.storeName ?? '店舗'}</Pill>
            )}
            <StatusPill status={persona.status} />
            {persona.isDefault && <Pill tone="ok">既定</Pill>}
          </div>
        </div>
      </div>

      {/*
        Their own voice. The same text the model receives, shown as a quotation -
        which is what makes a page of fields read as a person, and is the one
        element of the standard persona layout this data already had.
      */}
      {quote !== '' && <blockquote className="psheet__quote">{quote}</blockquote>}

      <dl className="psheet__facts">
        {sections.map(([title, value, note]) => (
          <div key={title}>
            <dt>
              {title}
              <span className="muted small" style={{ display: 'block', fontWeight: 400 }}>
                {note}
              </span>
            </dt>
            <dd>{value === '' ? <span className="muted">未記入</span> : value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function Personas() {
  const { items, suggestions } = useLoaderData<{
    items: Persona[];
    suggestions: ListResponse<PlatformDefaultItem>;
  }>();
  const session = useOutletContext<Session>();
  return <PersonasPanel items={items} suggestions={suggestions} base="" scope={sessionScope(session)} />;
}

/**
 * The screen itself, on the clinic's route and on the JMC clinic page alike.
 * See fact-panel.ts for the three things that differ between the two.
 */
export function PersonasPanel({
  items: rows,
  suggestions,
  base,
  scope,
  lead,
}: FactPanelProps<Persona> & { suggestions: ListResponse<PlatformDefaultItem> }) {
  const revalidator = useRevalidator();
  const { isClinicAdmin, stores: ownStores, activeStore } = scope;

  /*
   * Where a new persona goes.
   *
   * A persona is a fact like a treatment or a strength: clinic-wide, or one
   * store's. This screen could express neither - it never sent a store, so a
   * clinic admin could only ever create shared personas and a store user was
   * refused outright by the server's scope check.
   *
   * Empty is clinic-wide, which only a clinic admin may write.
   */
  const [newScope, setNewScope] = useState<string>(isClinicAdmin ? '' : (ownStores[0]?.id ?? ''));

  /*
   * Which personas the list shows. Same control, same URL parameter and same
   * defaults as the other fact screens - a person moving between them should not
   * have to learn a second way of asking the same question.
   */
  const [params, setParams] = useSearchParams();
  const viewScope = params.get('scope') ?? (activeStore ? 'effective' : 'all');
  const items = rows.filter((row) => inScope(row, viewScope, activeStore?.id));
  const scopeLabelId = useId();

  const setViewScope = (next: string) => {
    const query = new URLSearchParams(params);
    if (next === (activeStore ? 'effective' : 'all')) query.delete('scope');
    else query.set('scope', next);
    setParams(query);
  };

  const scopeTabs = activeStore
    ? [
        { id: 'effective', label: `共通 ＋ ${activeStore.name}` },
        { id: 'clinic', label: 'クリニック共通' },
        { id: 'store', label: activeStore.name },
      ]
    : [
        { id: 'all', label: '共通 ＋ 全店舗' },
        { id: 'clinic', label: 'クリニック共通' },
      ];

  /** null = closed, 'new' = creating, otherwise the persona being edited. */
  const [open, setOpen] = useState<'new' | Persona | null>(null);
  /*
   * The persona being READ, which is a different act from editing one.
   *
   * A tile used to open the form directly: eight inputs, every field equally
   * loud, and no way to simply look at the reader you are about to write for.
   * The sheet opens first and 編集 is a button on it - so the common case, "which
   * of these am I writing for", costs no thought at all.
   */
  const [viewing, setViewing] = useState<Persona | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);

  const editing = open !== 'new' && open !== null ? open : null;
  /*
   * Placeholders, but only while there is genuinely nothing to show.
   *
   * See Skeleton.tsx: a background refresh keeps the tiles on screen, because
   * replacing what somebody is reading with grey boxes throws away what they
   * were reading.
   */
  const pending = usePendingScreen();

  /**
   * What JMC offers that this clinic has not taken yet.
   *
   * A suggestion already copied is dropped from the list: the copy is the
   * clinic's own persona, sitting in the grid above, and offering it again is
   * how somebody ends up with four identical rows. Adopting the same template
   * twice is still allowed from the API - a clinic may deliberately want two
   * personas from one starting point - it is just not what the screen suggests.
   */
  const taken = new Set(
    items
      .map((persona) => persona.originDefaultId)
      .filter((id): id is string => typeof id === 'string'),
  );
  const offered = (suggestions.items ?? []).filter((row) => !taken.has(row.id));
  const [adopting, setAdopting] = useState<string | null>(null);

  async function adopt(suggestion: PlatformDefaultItem) {
    setAdopting(suggestion.id);
    try {
      await post(`${base}/defaults/${suggestion.id}/adopt`, {});
      await revalidator.revalidate();
    } finally {
      setAdopting(null);
    }
  }

  function start(target: 'new' | Persona) {
    setForm(target === 'new' ? EMPTY : formFor(target));
    setDraftError(null);
    setViewing(null);
    setOpen(target);
  }

  /**
   * Ask the model to fill in the descriptive fields.
   *
   * Only the three the writer would otherwise labour over. The label stays
   * theirs - it is the thing they already had in mind when they opened this -
   * and age and gender are only filled in if they left them blank, because
   * overwriting a deliberate answer with a guess is worse than leaving a gap.
   */
  async function draft() {
    setDrafting(true);
    setDraftError(null);
    try {
      const suggestion = await post<{
        ageRange: string;
        gender: string;
        concerns: string;
        tone: string;
        notes: string;
      }>(`${base}/personas/draft`, {
        label: form.label,
        ageRange: form.ageRange || undefined,
        gender: form.gender || undefined,
        brief: form.aiBrief || undefined,
      });
      setForm((current) => ({
        ...current,
        ageRange: current.ageRange || suggestion.ageRange,
        gender: current.gender || suggestion.gender,
        concerns: suggestion.concerns,
        tone: suggestion.tone,
        notes: suggestion.notes,
      }));
    } catch (err) {
      setDraftError(
        err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : '生成できませんでした',
      );
    } finally {
      setDrafting(false);
    }
  }

  const save = () => {
    const payload = {
      label: form.label,
      ageRange: form.ageRange,
      gender: form.gender,
      concerns: form.concerns,
      tone: form.tone,
      notes: form.notes,
      aiBrief: form.aiBrief,
    };
    if (editing) return patch(`${base}/personas/${editing.id}`, payload);
    // Absent means clinic-wide, which the server refuses from anybody who is not
    // a clinic admin. Moving an existing persona between scopes is deliberately
    // not offered: it changes what other stores' articles can use.
    return post(`${base}/personas`, newScope ? { ...payload, storeId: newScope } : payload);
  };

  /** Draw the portrait. Saved immediately, because reviewing an image is looking at it. */
  async function drawPortrait() {
    if (!editing) return;
    setDrafting(true);
    setDraftError(null);
    try {
      await post(`${base}/personas/${editing.id}/portrait`, {});
      await revalidator.revalidate();
    } catch (err) {
      setDraftError(
        err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : '生成できませんでした',
      );
    } finally {
      setDrafting(false);
    }
  }

  /**
   * Approve a persona, or send it back to draft.
   *
   * Only APPROVED personas reach a generation, and this screen - the one the
   * navigation sends people to - had no way to approve one. A persona created
   * here therefore stayed invisible to the wizard, with nothing on screen saying
   * why: the control existed only on the generic fact screens, which nothing
   * links to for personas.
   *
   * Its own action rather than part of 保存する, because approval is the gate the
   * whole product rests on. Editing the words a persona is described in and
   * declaring it fit to write articles from are two different decisions, and
   * bundling them means one is made by accident.
   */
  /**
   * Approve from the sheet, where the decision is actually made.
   *
   * Takes the persona rather than reading `editing`, because the sheet is open
   * and the form is not - and it re-reads afterwards so the pill on the sheet
   * changes under the press instead of going stale until the dialog is closed.
   */
  async function approve(persona: Persona | null, status: 'APPROVED' | 'DRAFT') {
    if (!persona) return;
    setDrafting(true);
    try {
      await patch(`${base}/personas/${persona.id}`, { status });
      await revalidator.revalidate();
      setViewing({ ...persona, status });
    } finally {
      setDrafting(false);
    }
  }

  async function setStatus(status: 'APPROVED' | 'DRAFT') {
    if (!editing) return;
    setDrafting(true);
    setDraftError(null);
    try {
      await patch(`${base}/personas/${editing.id}`, { status });
      await revalidator.revalidate();
    } catch (err) {
      setDraftError(
        err instanceof ApiError
          ? [err.message, ...err.fields].join(' / ')
          : '状態を変更できませんでした',
      );
    } finally {
      setDrafting(false);
    }
  }

  async function removePersona() {
    if (!editing) return;
    // It had no confirmation at all, and removed with one click.
    const ok = await confirmDialog({
      title: 'このペルソナを削除しますか？',
      message: `「${editing.label ?? 'このペルソナ'}」を削除します。元に戻せません。`,
      confirmLabel: '削除する',
      danger: true,
    });
    if (!ok) return;
    // /permanent - the bare DELETE only retires. See the same note in Ctas.tsx.
    await del(`${base}/personas/${editing.id}/permanent`);
    setOpen(null);
    await revalidator.revalidate();
  }

  return (
    <Card
      lead={lead}
      title="ペルソナ一覧"
      caption="記事の想定読者です。承認したペルソナだけが記事生成で選べます。クリニック共通は全店舗で、店舗のものはその店舗だけで使えます。"
    >
      {(isClinicAdmin || activeStore) && (
        <div className="scopebar">
          <span className="scopebar__label" id={scopeLabelId}>
            表示
          </span>
          <div className="scopebar__options" role="group" aria-labelledby={scopeLabelId}>
            {scopeTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className="scopeopt"
                aria-pressed={tab.id === viewScope}
                onClick={() => setViewScope(tab.id)}
              >
                {tab.label}
                {tab.id === viewScope && <span className="scopeopt__count">{items.length}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/*
        A grid, with the way to add one IN it.

        The add button was a full-width bar under the list, which put it further
        away the more personas a clinic had and made it the widest, loudest thing
        on a screen whose subject is the people above it. As a tile it is the same
        size as its neighbours, and it comes FIRST: at the end it moved down the
        page with every persona added, so the one action a new clinic needs was
        the one furthest from where the screen opens.
      */}
      {pending ? (
        <SkeletonPeople />
      ) : (
        <div className="pgrid">
          <button className="ptile ptile--add" type="button" onClick={() => start('new')}>
            <span className="ptile__plus" aria-hidden>
              ＋
            </span>
            <span className="ptile__label">新しいペルソナ</span>
            <span className="ptile__desc">名称だけ決めれば、悩みと文体はAIが下書きします。</span>
          </button>

          <AnimatePresence mode="popLayout">
            {items.map((persona, index) => (
              <PersonaTile
                key={persona.id}
                persona={persona}
                index={index}
                onOpen={() => setViewing(persona)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/*
        JMC's suggestions, under the clinic's own.

        Below rather than mixed in: these are not this clinic's personas yet, and
        a grid where half the tiles are somebody else's is a grid nobody can
        count. 「使う」 copies one in as a DRAFT - from that moment it is theirs to
        edit, and approving it is still a separate decision, exactly as for one
        they typed.
      */}
      {offered.length > 0 && (
        <>
          <p className="fieldgroup" style={{ marginTop: 22 }}>
            <span className="fieldgroup__title">JMCのおすすめ</span>
            <span className="fieldgroup__note">
              使うと、このクリニックの下書きとして取り込まれます。内容はあとから自由に変更できます。
            </span>
          </p>
          <div className="pgrid">
            {offered.map((suggestion) => (
              <div key={suggestion.id} className="ptile ptile--offer">
                <span className="ptile__label">{text(suggestion.payload.label) || suggestion.label}</span>
                {(text(suggestion.payload.ageRange) || text(suggestion.payload.gender)) && (
                  <span className="ptile__facts">
                    {[text(suggestion.payload.ageRange), text(suggestion.payload.gender)]
                      .filter(Boolean)
                      .map((fact) => (
                        <span key={fact} className="tag">
                          {fact}
                        </span>
                      ))}
                  </span>
                )}
                <span className="ptile__desc">
                  {text(suggestion.payload.concerns) || suggestion.description}
                </span>
                <span className="ptile__meta">
                  <button
                    className="btn btn--sm"
                    type="button"
                    disabled={adopting === suggestion.id}
                    onClick={() => void adopt(suggestion)}
                  >
                    {adopting === suggestion.id ? '取り込み中…' : '使う'}
                  </button>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {items.length === 0 && (
        <p className="muted small" style={{ marginTop: 10, marginBottom: 0 }}>
          {viewScope === 'clinic'
            ? 'クリニック共通のペルソナはまだありません。'
            : viewScope === 'store'
              ? 'この店舗だけのペルソナはまだありません。共通のものは「クリニック共通」で確認できます。'
              : 'まだペルソナがありません。'}
        </p>
      )}

      {/*
        The persona as a sheet: read it, then decide to change it. See
        PersonaSheet for why reading and editing are two screens.
      */}
      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        // The sheet carries the name beside the face; a dialog title repeating it
        // is the same words twice, four centimetres apart.
        title="ペルソナ"
        caption="このペルソナで記事を書くと、下の内容がそのままAIに渡ります。"
        wide
      >
        {viewing && <PersonaSheet persona={viewing} />}
        <div className="editpanel__actions">
          <button className="btn btn--sm" type="button" onClick={() => viewing && start(viewing)}>
            編集する
          </button>
          {viewing?.status !== 'APPROVED' ? (
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={drafting}
              onClick={() => void approve(viewing, 'APPROVED')}
            >
              承認する
            </button>
          ) : (
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={drafting}
              onClick={() => void approve(viewing, 'DRAFT')}
            >
              下書きに戻す
            </button>
          )}
          {viewing?.status !== 'APPROVED' && (
            <span className="muted small">
              承認するまで、新規記事作成のペルソナ一覧には出てきません。
            </span>
          )}
        </div>
      </Modal>

      {/*
        The form, in a dialog like every other add on this product. It used to
        unfold under the list, which pushed the tiles down the moment it opened.
      */}
      <ModalForm
        open={open !== null}
        onClose={() => setOpen(null)}
        title={editing ? 'ペルソナを編集' : '新しいペルソナ'}
        caption={
          editing
            ? '保存しても状態は変わりません。承認はペルソナを開いたところから行います。'
            : '名称だけ決めれば、悩みと文体はAIが下書きします。作成したものは下書きです。'
        }
        wide
        onSubmit={save}
        submitLabel={editing ? '保存する' : '作成する'}
      >
        {/*
          Read top to bottom, in the order somebody fills it in: who this person
          is, then what to tell the model, then what the model came back with,
          then the picture. The panel's columns are auto-fitted, so anything that
          must not share a row says so - the alternative is a form whose reading
          order depends on the width of the window.
        */}
        <FieldGroup title="この人はだれか" note="一覧に並ぶ見出しと、基本の属性" />

        {open === 'new' &&
          (isClinicAdmin ? (
            <Field
              label="登録先"
              htmlFor="p-scope"
              span="full"
              hint="クリニック共通にすると、すべての店舗の記事で選べます。店舗を選ぶと、その店舗だけのペルソナになります。"
            >
              <Select
                id="p-scope"
                value={newScope}
                onChange={setNewScope}
                options={[
                  { value: '', label: 'クリニック共通（全店舗で使用）' },
                  ...ownStores.map((store) => ({ value: store.id, label: `${store.name}のみ` })),
                ]}
              />
            </Field>
          ) : (
            <p className="notice" role="status">
              登録先：
              <strong>
                {ownStores.find((store) => store.id === newScope)?.name ?? '担当店舗'}
              </strong>
              のペルソナとして登録されます。クリニック共通のペルソナはクリニック管理者が登録します。
            </p>
          ))}

        <Field
          label="名称"
          htmlFor="p-label"
          required
          span="wide"
          hint="一覧に表示される見出し。例：20代女性・美容意識高め"
        >
          <input
            id="p-label"
            value={form.label}
            maxLength={100}
            required
            placeholder="20代女性・美容意識高め"
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
        </Field>

        <Field label="年代" htmlFor="p-age" hint="記事の言葉選びに影響します。">
          <input
            id="p-age"
            value={form.ageRange}
            maxLength={40}
            placeholder="20代後半"
            onChange={(e) => setForm({ ...form, ageRange: e.target.value })}
          />
        </Field>

        <Field label="性別" htmlFor="p-gender" hint="指定しない場合は空欄で。">
          <input
            id="p-gender"
            value={form.gender}
            maxLength={40}
            placeholder="女性"
            onChange={(e) => setForm({ ...form, gender: e.target.value })}
          />
        </Field>

        <Field
          label="AIへの指示"
          htmlFor="p-brief"
          span="wide"
          hint="「共働き」「初回カウンセリング前」など。空欄でも生成できます。"
        >
          <input
            id="p-brief"
            value={form.aiBrief}
            maxLength={1000}
            placeholder="共働き、平日は時間が取れない"
            onChange={(e) => setForm({ ...form, aiBrief: e.target.value })}
          />
        </Field>

        {/*
          Inside the form but not a submit: these fill the fields below, and the
          writer still has to read them and press save.
        */}
        <div className="aiassist">
          <div className="aiassist__buttons">
            <button
              className="btn btn--sm"
              type="button"
              disabled={drafting || !form.label.trim()}
              onClick={() => void draft()}
            >
              {drafting ? '生成中…' : '✦ 悩み・文体を下書き'}
            </button>
            {editing && (
              <button
                className="btn btn--sm btn--ghost"
                type="button"
                disabled={drafting}
                onClick={() => void drawPortrait()}
              >
                ✦ 顔写真を生成
              </button>
            )}
          </div>
          <span className="aiassist__note">
            名称と上の指示をもとに、下の3つを埋めます。生成した内容はそのまま保存されません。
          </span>
          {draftError && (
            <p className="alert" role="alert">
              {draftError}
            </p>
          )}
        </div>

        <FieldGroup title="記事の書き方" note="この3つがそのままAIに渡ります" />

        <Field
          label="悩み・関心"
          htmlFor="p-concerns"
          span="full"
          hint="この人が何を気にしていて、何をためらっているか。記事の切り口と、どの不安に先に答えるかがここで決まります。具体的な場面（朝の化粧、人に会う予定）まで書くと、記事が一般論から離れます。"
        >
          <Textarea
            id="p-concerns"
            minRows={5}
            value={form.concerns}
            maxLength={2000}
            placeholder="毛穴の開きが気になり、化粧で隠すのに時間がかかる。ダウンタイムの短さを重視している。"
            onChange={(e) => setForm({ ...form, concerns: e.target.value })}
          />
        </Field>

        <Field
          label="希望トーン"
          htmlFor="p-tone"
          span="wide"
          hint="この読者に向けた語り口。店舗の既定トーンより優先してAIに渡されます。例：やさしく、専門用語は避けて言い換える"
        >
          <input
            id="p-tone"
            value={form.tone}
            maxLength={200}
            onChange={(e) => setForm({ ...form, tone: e.target.value })}
          />
        </Field>

        <Field
          label="備考"
          htmlFor="p-notes"
          span="full"
          hint="この読者に対して書くときの注意点。触れてほしくない話題や、前提にしてよい知識のレベルなど。"
        >
          <Textarea
            id="p-notes"
            minRows={3}
            value={form.notes}
            maxLength={2000}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Field>

        {editing && (
          <>
            <FieldGroup title="顔写真" note="一覧のカードに表示されます" />
            <Field
              label="画像"
              htmlFor="p-portrait"
              span="full"
              hint="架空の人物のイメージです。実在の患者の写真は使用しないでください。"
            >
              <ImageUpload
                imageId={typeof editing.photoId === 'string' ? editing.photoId : null}
                endpoint={`${base}/personas/${editing.id}/portrait`}
                label="写真を選ぶ"
                placeholder={text(editing.label).charAt(0) || '人'}
                onDone={() => revalidator.revalidate()}
              />
            </Field>

            <div className="field field--full">
              <FieldGroup
                title="このペルソナの状態"
                note="承認したペルソナだけが記事作成で選べます"
              />
              <p className="muted small" style={{ margin: '0 0 8px' }}>
                現在: <StatusPill status={editing.status} />
              </p>
              <div className="chips">
                {editing.status !== 'APPROVED' ? (
                  <button
                    className="btn btn--sm"
                    type="button"
                    disabled={drafting}
                    onClick={() => void setStatus('APPROVED')}
                  >
                    承認する
                  </button>
                ) : (
                  <button
                    className="btn btn--sm btn--ghost"
                    type="button"
                    disabled={drafting}
                    onClick={() => void setStatus('DRAFT')}
                  >
                    下書きに戻す
                  </button>
                )}
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  onClick={() => void removePersona()}
                >
                  このペルソナを削除
                </button>
              </div>
              {editing.status !== 'APPROVED' && (
                <p className="muted small" style={{ marginTop: 6 }}>
                  下書きのままでは、新規記事作成のペルソナ一覧に表示されません。
                </p>
              )}
            </div>
          </>
        )}
      </ModalForm>
    </Card>
  );
}
